// The whole HTTP API contract (docs/ARCHITECTURE.md) as one TypeScript interface.
// Two implementations: http.ts (real backend) and mock.ts (in-memory demo).

import type {
  AdminOrgRow,
  AISystem,
  AuthRedeemResponse,
  AuthRequestResponse,
  CatalogEntry,
  CatalogPick,
  ChatEvent,
  ChatHistory,
  CheckAnswerResponse,
  CheckAnswers,
  CheckDraftResponse,
  ConfirmBulkRequest,
  ConfirmBulkResponse,
  DocDetailKey,
  DocDetailsView,
  DocumentInfo,
  PolicyActionInput,
  ImportFinished,
  ImportJob,
  ImportStarted,
  IntakeInput,
  JobStarted,
  LiteracyPlan,
  LiteracyState,
  MeResponse,
  Org,
  OrgProfile,
  OrgSuggestJob,
  OrgSuggestStarted,
  Overview,
  PilotAccess,
  PublicOrgName,
  PublicSurvey,
  PublicTraining,
  RulesSummary,
  Screening,
  SurveyAnalysis,
  SurveyInvitation,
  SurveyResponseInput,
  SurveyState,
  TokenKind,
  TrainingCheckInput,
  TrainingCheckResult,
  TrainingResult,
  TrainingSubmit,
} from "../types";

/** Only the changed fields; screening only the changed keys (the server deep-merges it). */
export type SystemPatch = Partial<Omit<AISystem, "id" | "triage" | "history" | "version" | "screening">> & {
  screening?: Partial<Screening>;
};

export interface Api {
  // auth
  me(): Promise<MeResponse>;
  authRequest(email: string, next?: string): Promise<AuthRequestResponse>;
  /** Redeems a magic-link token after an explicit click (POST /auth/redeem).
   *  410 {code: "used" | "expired"} when the link no longer works. */
  authRedeem(token: string): Promise<AuthRedeemResponse>;
  logout(): Promise<void>;

  // org
  /** Starts reading the company website in the background. Poll orgSuggestStatus every 2 s. */
  orgSuggest(domain: string): Promise<OrgSuggestStarted>;
  orgSuggestStatus(jobId: string): Promise<OrgSuggestJob>;
  orgCreate(profile: OrgProfile): Promise<Org>;
  orgPatch(patch: Partial<OrgProfile>): Promise<Org>;
  /** Replaces one share link; the old one stops working at once. */
  rotateToken(kind: TokenKind): Promise<Org>;
  /** Deletes all company data (DELETE /org). `confirmName` must match the company name. */
  deleteOrg(confirmName: string): Promise<void>;
  overview(): Promise<Overview>;

  // catalog / rules
  catalog(): Promise<CatalogEntry[]>;
  rulesSummary(): Promise<RulesSummary>;

  // register
  systems(): Promise<AISystem[]>;
  createSystem(system: Partial<AISystem>): Promise<AISystem>;
  systemsFromText(text: string): Promise<AISystem[]>;
  /** One entry per picked account (a product without account types: one entry). Skips what is
   *  already in the register (same product and account); answers with the new entries. */
  systemsFromCatalog(picks: CatalogPick[]): Promise<AISystem[]>;
  /** The finished entries (a bare list, or with row counts), or (file takes longer than 20 s)
   *  a job to poll with importStatus every 2 s. */
  importSystems(file: File): Promise<AISystem[] | ImportStarted | ImportFinished>;
  /** One status check; `signal` aborts it (the import poller gives every check 15 s). */
  importStatus(jobId: string, signal?: AbortSignal): Promise<ImportJob>;
  /** Stops a running import (POST /systems/import/{job_id}/cancel). Answers with the final state:
   *  "cancelled" (entries added before stay), or "ready"/"error" when it had finished already. */
  importCancel(jobId: string): Promise<ImportJob>;
  getSystem(id: string): Promise<AISystem>;
  /** Sends only the changed fields plus the version the person saw; 409 {code: "conflict"} when
   *  someone else saved the entry in between (reload it). */
  patchSystem(id: string, patch: SystemPatch, expectedVersion: number | undefined): Promise<AISystem>;
  deleteSystem(id: string): Promise<void>;
  confirmSystem(id: string): Promise<AISystem>;
  /** Confirms many proposals at once: the given ids, or every proposal with the given light. */
  confirmBulk(body: ConfirmBulkRequest): Promise<ConfirmBulkResponse>;
  retriageSystem(id: string): Promise<AISystem>;
  setActionDone(id: string, actionId: string, done: boolean): Promise<AISystem>;

  // agent check (anonymous or builder link)
  /** builder link: the optional name and e-mail become the suggested owner the AI owner sees */
  checkDraft(description: string, builderToken?: string, builder?: { name?: string; email?: string }): Promise<CheckDraftResponse>;
  checkAnswer(checkId: string, answers: CheckAnswers): Promise<CheckAnswerResponse>;
  checkSave(checkId: string): Promise<AISystem>;
  checkCardPath(checkId: string): string;

  // survey + intake
  survey(): Promise<SurveyState>;
  /** Starts the analysis in the background ({status: "running"}); poll survey() for analysis_status.
   *  Older servers answer with the finished analysis. */
  surveyAnalyze(): Promise<JobStarted | SurveyAnalysis>;
  /** AI-drafted invitation text for staff. */
  surveyInvitation(): Promise<SurveyInvitation>;
  /** `revision` = analysis_revision of the shown analysis (409 {code: "conflict"} when it is stale). */
  surveyAcceptProposal(index: number, revision: number | null | undefined): Promise<AISystem>;
  publicSurvey(token: string): Promise<PublicSurvey>;
  submitSurvey(token: string, response: SurveyResponseInput): Promise<{ ok: boolean }>;
  publicIntake(token: string): Promise<PublicOrgName>;
  submitIntake(token: string, body: IntakeInput): Promise<{ ok: boolean }>;
  publicBuilder(token: string): Promise<PublicOrgName>;

  // literacy
  literacy(): Promise<LiteracyState>;
  /** Starts the plan in the background ({status: "running"}); poll literacy() for plan_status.
   *  Older servers answer with the finished plan. */
  literacyPlan(): Promise<JobStarted | LiteracyPlan>;
  /** The module and quiz without the answer key. */
  publicTraining(token: string): Promise<PublicTraining>;
  /** Instant feedback for one quiz answer (the answer key stays on the server). */
  checkTrainingAnswer(token: string, body: TrainingCheckInput): Promise<TrainingCheckResult>;
  submitTraining(token: string, body: TrainingSubmit): Promise<TrainingResult>;

  // documents
  documents(): Promise<DocumentInfo[]>;
  /** Values for the fill-in fields, what is missing, the policy's adoption, agent cards. */
  docDetails(): Promise<DocDetailsView>;
  /** Save values (null clears one); a saved value is no longer a suggestion. */
  saveDocDetails(values: Partial<Record<DocDetailKey, string | number | null>>): Promise<DocDetailsView>;
  /** Fill empty values from what the app knows; with a website a background reader adds what the site says. */
  suggestDocDetails(): Promise<DocDetailsView>;
  policyAction(body: PolicyActionInput): Promise<DocDetailsView>;
  /** The owner's or the AI owner's confirmation of an agent card (instead of a signature). */
  confirmCard(id: string, role: "owner" | "admin", confirmed: boolean): Promise<AISystem>;
  /** Starts a file download for an /api path (for example "/documents/register"). */
  download(path: string, filename: string): Promise<void>;

  // admin
  adminOrgs(): Promise<AdminOrgRow[]>;
  /** Closed pilot access list ("Dostop do pilota"): env entries + entries added here. */
  adminAccess(): Promise<PilotAccess>;
  adminAccessAdd(entry: string, note?: string): Promise<PilotAccess>;
  adminAccessRemove(entry: string): Promise<PilotAccess>;

  // guided conversation (SSE)
  /** One turn. `picks` answers a catalog picker (the server adds them itself and writes the user's
   *  line); `message` then carries only the tools that are not in the catalog, or "". */
  chat(message: string, onEvent: (e: ChatEvent) => void, signal?: AbortSignal, picks?: CatalogPick[]): Promise<void>;
  /** Server-side history (GET /chat). Empty messages means a new conversation. */
  chatHistory(): Promise<ChatHistory>;
}

export class ApiError extends Error {
  status: number;
  /** machine-readable reason from the server, e.g. "revoked" (403), "used" / "expired" (410) */
  code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

/** 403 {code: "revoked"}: pilot access was removed; the client drops its state. */
export function isRevoked(e: unknown): boolean {
  return e instanceof ApiError && e.status === 403 && e.code === "revoked";
}

/** 409 "Vnos je medtem spremenil nekdo drug. Osvežite.": someone else saved the entry first. */
export function isConflict(e: unknown): boolean {
  return e instanceof ApiError && e.status === 409 && (e.code === "conflict" || /spremenil nekdo drug/i.test(e.message));
}
